import {logger, ObjUtil, Time, Util} from 'yougile-util'
import {DataObjectId, Hub, HubProperties, SandboxTree} from 'yougile-model'
import {UIRegistry} from '@api/model/ui-registry'
import {DesktopRenderer} from '@client/desktop-renderer'
import {ExtensionClient} from '@client/extension-client'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Backend} from '@common/backend'
import {showConfirm} from '@components/common/base/notifications'
import {crmStore} from '@components/crm/crm-store'
import {extensionsStore} from '@components/extensions-gallery/extensions-store'
import {globals} from '@extensions/globals-base'
import {ShowMessageBox} from '../types/message-box'
import {CompileScript, ContextObjectStore, Runner} from './runner'

let devMode = false

interface ReloadListener {
  timestamp: number
  listenerId: number
}

type ChooseFile = (onFileChoose: (file: File) => void) => void

export class Api {
  private static reloadListeners: Map<DataObjectId, ReloadListener> = new Map()
  private static chooseFile: ChooseFile
  private static showMessageBox: ShowMessageBox

  private static enabledExtensions: {[extensionId: string]: true} = {}

  public static isInDevMode(): boolean {
    return devMode
  }

  public static isExtensionEnabled(id: string): boolean {
    return !!this.enabledExtensions[id]
  }

  public static setDevMode(isTurnedOn: boolean): void {
    devMode = isTurnedOn
  }

  private static getContainer(box: SandboxTree): Hub {
    Util.assert(!!box, 'Current company is not set')
    let container: Hub = box.findHubByTitle('.scripts', box.companyId, true)
    if (!container) {
      container = new Hub(box.companyId, '.scripts')
      box.add(container, box.companyId, false, false)
    }
    return container
  }

  public static addScript(script: Hub): void {
    const box = Sync.get(script)
    const container = Api.getContainer(box)
    let name = script.title
    let i = 1
    while (box.findHubByTitle(name, container.id, true) && i < 200) {
      i++
      name = script.title.split('.js').shift() + i + '.js'
    }
    script.title = name
    box.add(script, container.id, false, false)
  }

  public static findScript(box: SandboxTree, title: string): Hub {
    const container = Api.getContainer(box)
    return box.findHubByTitle(title, container.id, true)
  }

  public static listScripts(box: SandboxTree): Hub[] {
    const container = Api.getContainer(box)
    const scripts = box.getChildrenHubs(container.id)
    return scripts
  }

  public static saveScript(script: Hub): void {
    script.commit(true)
  }

  public static listenForReload(box: SandboxTree): void {
    if (!box) {
      return
    }
    const cnt = Api.getContainer(box)
    if (!cnt) {
      return
    }
    if (Api.reloadListeners.has(box.companyId)) {
      return
    }
    const listenerId = SyncEvents.addListener(box.companyId, cnt.id, () => {
      const listener = Api.reloadListeners.get(box.companyId)
      const oldTs = listener.timestamp
      const newTs = +cnt.properties[HubProperties.scriptsReloadTimestamp] || 0
      if (newTs > oldTs) {
        void Api.reloadAllScriptsInCompany(box)
        listener.timestamp = newTs
      }
    })
    Api.reloadListeners.set(box.companyId, {
      timestamp: +cnt.properties[HubProperties.scriptsReloadTimestamp] || 0,
      listenerId
    })
  }

  static loadScript(box: SandboxTree, cb?: () => void): void {
    Api.chooseFile((file: File) => {
      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = async () => {
        if (file.name.indexOf('.js') !== file.name.length - 3) {
          Api.showMessageBox('Please, choose .js file', 'warning')
          return
        }
        let script = Api.findScript(box, file.name)
        if (!script) {
          script = new Hub(box.companyId, file.name)
          Api.addScript(script)
          script.description = reader.result as string
          Api.enableScript(script)
          Api.saveScript(script)
          if (cb) {
            cb()
          }
        } else {
          if (await showConfirm(`Script ${file.name} exists, overwrite?`)) {
            script.description = reader.result as string
            Api.saveScript(script)
            if (cb) {
              cb()
            }
          }
        }
        logger.log(`Loaded script with name = "${file.name}"`)
      }
    })
  }

  static async reloadAllScriptsInCompany(box: SandboxTree): Promise<void> {
    if (SharedBoardsClient.getId()) {
      return
    }
    if (!box || box.companyId !== globals.getCompanyId()) {
      return
    }
    const pluginPrefix = 'yg-plugin-'
    const container = Api.getContainer(box)
    const extensions = await ExtensionClient.getScripts(box.companyId)
    logger.log(
      `Current appVersion=${Backend.appVersion}, desktopVersion=${globals.desktopVersion}`
    )

    Runner.unloadAll()
    box.getChildrenHubs(container.id).forEach((s: Hub) => {
      if (Api.isEnabledScript(s) && !s.title.startsWith(pluginPrefix)) {
        Runner.runJs(s.description, s.title, box.companyId, undefined)
      }
    })

    this.enabledExtensions = {}
    ObjUtil.forEach(extensions, (extId, scripts) => {
      for (const scr of scripts) {
        if (scr.enabled) {
          this.enabledExtensions[extId] = true
          const name = pluginPrefix + extId + '-' + scr.name
          logger.log(
            'Running plugin',
            name + ` ${scr.version ?? ''}` + ` ${scr.minClientVersion ?? ''}`
          )
          Runner.runJs(scr.script, name, box.companyId, String(extId))
        }
      }
    })

    crmStore.updateCrmEnabled(!!this.enabledExtensions['crm'])

    const uiReg = UIRegistry.get(globals.getCompanyId())
    const extensionsWithSubscriptions = uiReg.extensionRenderers?.keys()
    for (const ext of extensionsWithSubscriptions) {
      if (!extensions[ext]) {
        uiReg.clearExtensionRenderers(ext)
      }
    }
  }

  public static reloadScriptsForAll(): void {
    const box = Sync.getCurrentSandbox()
    if (!box.users.get(globals.currentUserId)?.isAdmin) {
      return
    }
    const cnt = Api.getContainer(box)
    if (!cnt) {
      return
    }
    cnt.properties[HubProperties.scriptsReloadTimestamp] = Time.now()
    cnt.commit()
  }

  public static enableScript(script: Hub): void {
    script.properties[HubProperties.enabledScript] = true
    Api.saveScript(script)
  }

  public static disableScript(script: Hub): void {
    script.properties[HubProperties.enabledScript] = false
    Api.saveScript(script)
    if (script.title) {
      const uiReg = UIRegistry.get(globals.getCompanyId())
      uiReg.clearExtensionRenderers(script.title)
    }
  }

  public static toggleEnableScript(script: Hub): void {
    if (Api.isEnabledScript(script)) {
      Api.disableScript(script)
    } else {
      Api.enableScript(script)
    }
  }

  public static isEnabledScript(script: Hub): boolean {
    const {enabledScript} = HubProperties
    return true === script.properties[enabledScript]
  }

  public static init(
    chooseFile: ChooseFile,
    showMessageBox: ShowMessageBox,
    contextObjectStore: ContextObjectStore,
    compileScript: CompileScript = Util.identity,
    addReloadListeners: (reload: () => void) => void = Util.noop
  ) {
    Runner.init(contextObjectStore, showMessageBox, compileScript)

    Api.chooseFile = chooseFile
    Api.showMessageBox = showMessageBox

    const reload = (): void => {
      const box = Sync.getCurrentSandbox()

      if (!box) {
        return
      }

      if (!Util.isDesktop()) {
        void Api.reloadAllScriptsInCompany(box)
        Api.listenForReload(box)
        return
      }

      DesktopRenderer.reqDesktopVersionUpdate()
      setTimeout(async () => {
        void Api.reloadAllScriptsInCompany(box)
        Api.listenForReload(box)
      }, 2000)
    }

    globals.onCompanyChange(reload)

    extensionsStore.init()

    addReloadListeners(reload)

    reload()
  }
}
